import { Close } from '@mui/icons-material';
import { Box, Chip, Dialog, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material';
import { endOfDay } from 'date-fns';
import { MouseEvent, PropsWithChildren, useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { t } from '../../constants/translations';
import { useAdmin } from '../../contexts/admin.context';
import { useLocale } from '../../contexts/locale.context';
import PrimaryButton from '../PrimaryButton';

type Props = {
  checkPlan: 'plus' | 'pro' | 'lite';
};

const PlanCheckPage = ({ checkPlan, children }: PropsWithChildren<Props>) => {
  const { isPlus, isPro, cafeSlug, cafe } = useAdmin();
  const { lang } = useLocale();
  const navigate = useNavigate();

  const isAvailable = useMemo(
    () =>
      isPro ||
      (checkPlan === 'plus' && isPlus) ||
      (checkPlan === 'lite' && endOfDay(cafe?.plan?.lastDate || 1709247600000).getTime() > Date.now()),
    [cafe?.plan?.lastDate, checkPlan, isPlus, isPro]
  );

  if (isAvailable) {
    return <div>{children}</div>;
  }

  return (
    <Box sx={{ maxWidth: 500 }} m="0 auto">
      <Typography sx={{ mb: 3 }} align="center">
        {t.interested_in_feature[lang]}
        {checkPlan === 'pro' && t.feature_available_pro[lang]}
        {checkPlan === 'plus' && t.feature_available_plus_pro[lang]}
        {checkPlan === 'lite' && t.plan_expired[lang]}
      </Typography>

      <Typography sx={{ mb: 3 }} align="center">
        {checkPlan === 'pro' && t.upgrade_to_pro[lang]}
        {checkPlan === 'plus' && t.upgrade_to_plus_pro[lang]}
      </Typography>

      <Box sx={{ textAlign: 'center' }}>
        <PrimaryButton onClick={() => navigate(`/${cafeSlug}/restaurant?tab=plan`)} variant="contained">
          {t.upgrade_plan[lang]}
        </PrimaryButton>
      </Box>
    </Box>
  );
};

const PlanCheckModal = ({ checkPlan, children }: PropsWithChildren<Props>) => {
  const { isPlus, isPro, isLite, cafeSlug, cafe } = useAdmin();
  const { lang } = useLocale();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);

  const isAvailable = useMemo(
    () =>
      isPro ||
      (checkPlan === 'plus' && isPlus) ||
      (checkPlan === 'lite' && endOfDay(cafe?.plan?.lastDate || 0).getTime() > Date.now()),
    [cafe?.plan?.lastDate, checkPlan, isPlus, isPro]
  );

  const handleClick = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      if (isAvailable) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      setIsOpen(true);
    },
    [isAvailable]
  );

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const goToUpdate = useCallback(() => {
    setIsOpen(false);
    navigate(`/${cafeSlug}/restaurant?tab=plan`);
  }, [cafeSlug, navigate]);

  return (
    <>
      <Box onClick={handleClick} sx={{ position: 'relative' }}>
        {children}
        {checkPlan === 'plus' && !isPlus && !isPro && (
          <Chip
            sx={{ position: 'absolute', top: 0, right: 0, ml: 1, height: 16, fontSize: 10 }}
            label="PLUS"
            size="small"
            color="secondary"
            variant="outlined"
          />
        )}
        {checkPlan === 'pro' && !isPro && (
          <Chip
            sx={{ position: 'absolute', top: 0, right: 0, ml: 1, height: 16, fontSize: 10 }}
            label="PRO"
            size="small"
            color="primary"
            variant="outlined"
          />
        )}
      </Box>

      <Dialog open={isOpen} onClose={handleClose}>
        <DialogTitle sx={{ textAlign: 'right' }}>
          <IconButton onClick={handleClose}>
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <Typography sx={{ mb: 3 }} align="center">
            {t.interested_in_feature[lang]}
            {checkPlan === 'pro' && t.feature_available_pro[lang]}
            {checkPlan === 'plus' && t.feature_available_plus_pro[lang]}
            {checkPlan === 'lite' && t.plan_expired[lang]}
          </Typography>

          <Typography sx={{ mb: 3 }} align="center">
            {checkPlan === 'pro' && t.upgrade_to_pro[lang]}
            {checkPlan === 'plus' && t.upgrade_to_plus_pro[lang]}
          </Typography>

          <Box sx={{ textAlign: 'center' }}>
            <PrimaryButton onClick={goToUpdate} variant="contained">
              {t.upgrade_plan[lang]}
            </PrimaryButton>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const ProPage = ({ children }: PropsWithChildren) => <PlanCheckPage checkPlan="pro">{children}</PlanCheckPage>;

export const PlusPage = ({ children }: PropsWithChildren) => <PlanCheckPage checkPlan="plus">{children}</PlanCheckPage>;

export const LitePage = ({ children }: PropsWithChildren) => <PlanCheckPage checkPlan="lite">{children}</PlanCheckPage>;

export const LiteFeature = ({ children }: PropsWithChildren) => (
  <PlanCheckModal checkPlan="lite">{children}</PlanCheckModal>
);

export const PlusFeature = ({ children }: PropsWithChildren) => (
  <PlanCheckModal checkPlan="plus">{children}</PlanCheckModal>
);

export const ProFeature = ({ children }: PropsWithChildren) => (
  <PlanCheckModal checkPlan="pro">{children}</PlanCheckModal>
);
